/**
 * What an organization poster IS — pure data, no DOM, no canvas, no i18n.
 *
 * Shared by the editor (authoring), the renderer (drawing), the exports
 * (file names, CSV) and the scan redirect in `api/p/[code].ts` (the tracked
 * destination), so every surface agrees on one URL and one slug rule.
 *
 * Poster units are CSS px at 96 dpi; the renderer scales for preview and
 * for the 300 dpi export.
 */

export const POSTER_TEMPLATES = ["volunteers", "creators", "event"] as const;
export type PosterTemplate = (typeof POSTER_TEMPLATES)[number];

export const POSTER_LANGS = ["en", "es", "pt"] as const;
export type PosterLang = (typeof POSTER_LANGS)[number];

export const PAPER = {
  letter: { w: 816, h: 1056, pdf: "letter", label: "US Letter" },
  a4: { w: 794, h: 1123, pdf: "a4", label: "A4" },
} as const;
export type PaperSize = keyof typeof PAPER;

export type DecorKind = "sunny" | "cool";

export const BADGE_SHAPES = ["circle", "square", "heart", "triangle", "diamond", "hexagon", "star", "pentagon"] as const;
export type BadgeShape = (typeof BADGE_SHAPES)[number];

export const BADGE_COLORS = {
  yellow: { fill: "#f7c933", txt: "#10183c" },
  blue: { fill: "#1a6ccb", txt: "#ffffff" },
  pink: { fill: "#ee5873", txt: "#ffffff" },
  navy: { fill: "#10183c", txt: "#f9f4ea" },
  sky: { fill: "#3ca0e9", txt: "#10183c" },
  white: { fill: "#fffdf8", txt: "#10183c" },
  rose: { fill: "#f590a4", txt: "#10183c" },
  sand: { fill: "#fce7a6", txt: "#10183c" },
} as const;
export type BadgeColor = keyof typeof BADGE_COLORS;
const BADGE_COLOR_KEYS = Object.keys(BADGE_COLORS) as BadgeColor[];

export const BADGE_SIZES = { s: 40, m: 48, l: 64 } as const;
export type BadgeSize = keyof typeof BADGE_SIZES;

export interface PosterCategory {
  /** Venue category, e.g. "cafes". Slugified into utm_campaign. */
  name: string;
  /** How many venues get this variant; drives the reference sheet's "Placed ___ / N". */
  venues: number;
  shape: BadgeShape;
  color: BadgeColor;
}

export interface PosterCopy {
  /** Rows are lines; `*word*` is the brush accent. A row holding only a starred word prints extra large. */
  headline: string;
  /** Bold line under the headline. */
  sub: string;
  /** Comma- or newline-separated torn-paper tags. */
  chips: string;
  /** Optional small note under the tags. */
  note: string;
  /** Draw Rodri beside the note. */
  rodri: boolean;
  /** Yellow sticker above the call to action, e.g. "Live now". */
  sticker: string;
  /** Call-to-action lead; the link itself is derived from the destination. */
  ctaLead: string;
  /** Caption under the QR card. */
  caption: string;
}

/** The event template's date/place block, drawn only when at least one is set. */
export interface PosterEventBlock {
  date: string;
  place: string;
}

export interface PosterConfig {
  version: 1;
  template: PosterTemplate;
  lang: PosterLang;
  paper: PaperSize;
  decor: DecorKind;
  copy: PosterCopy;
  event: PosterEventBlock;
  /** "Hosted by <org>" beside the weXare lockup. */
  hostedBy: boolean;
  badge: { show: boolean; size: BadgeSize };
  cats: PosterCategory[];
  /** utm_campaign: the variant's category, or one fixed value for every variant. */
  campaign: { mode: "category" | "fixed"; fixed: string };
}

/** One printed poster: a category variant, or the single un-badged poster. */
export interface PosterVariant {
  cat: PosterCategory | null;
  /** 1-based badge number; 0 for the single poster. */
  num: number;
}

/* ───────────────────────── presets ───────────────────────── */

const SUNNY_CATS: PosterCategory[] = [
  { name: "cafes", venues: 10, shape: "circle", color: "yellow" },
  { name: "shops", venues: 10, shape: "square", color: "blue" },
  { name: "community", venues: 10, shape: "heart", color: "pink" },
  { name: "faith", venues: 5, shape: "triangle", color: "navy" },
  { name: "learning", venues: 5, shape: "diamond", color: "sky" },
  { name: "services", venues: 5, shape: "hexagon", color: "white" },
  { name: "sports", venues: 5, shape: "star", color: "rose" },
];

interface Preset {
  decor: DecorKind;
  copy: PosterCopy;
}

const PRESETS: Record<PosterTemplate, Record<PosterLang, Preset>> = {
  volunteers: {
    en: {
      decor: "cool",
      copy: {
        headline: "No experience.\nNo *idea* where\nto start?",
        sub: "Your first step: one real project, one real reference.",
        chips: "Unpaid volunteer work, No experience needed, Every project builds your resume",
        note: "",
        rodri: false,
        sticker: "Live now",
        ctaLead: "Find your first quest at",
        caption: "Scan to join",
      },
    },
    es: {
      decor: "cool",
      copy: {
        headline: "¿Sin experiencia?\n¿Sin *idea* de\npor dónde empezar?",
        sub: "Tu primer paso: un proyecto real, una referencia real.",
        chips: "Voluntariado sin paga, No necesitás experiencia, Cada proyecto suma a tu CV",
        note: "",
        rodri: false,
        sticker: "En vivo",
        ctaLead: "Encontrá tu primer quest en",
        caption: "Escaneá para sumarte",
      },
    },
    pt: {
      decor: "cool",
      copy: {
        headline: "Sem experiência?\nSem *ideia* de\npor onde começar?",
        sub: "Seu primeiro passo: um projeto real, uma referência real.",
        chips: "Voluntariado sem pagamento, Não precisa de experiência, Cada projeto conta no seu currículo",
        note: "",
        rodri: false,
        sticker: "No ar",
        ctaLead: "Encontre seu primeiro quest em",
        caption: "Escaneie para participar",
      },
    },
  },
  creators: {
    en: {
      decor: "sunny",
      copy: {
        headline: "Need more\n*hands?*",
        sub: "Post the task. People show up to help.",
        chips: "Park cleanups, Food drives, Event setup, Flyers",
        note: "Our AI helps plan it.",
        rodri: true,
        sticker: "Live now",
        ctaLead: "Post a quest at",
        caption: "Scan to post",
      },
    },
    es: {
      decor: "sunny",
      copy: {
        headline: "¿Necesitás más\n*manos?*",
        sub: "Publicá la tarea. La gente viene a ayudar.",
        chips: "Limpieza de plazas, Colectas, Armado de eventos, Volantes",
        note: "Nuestra IA te ayuda a planificarlo.",
        rodri: true,
        sticker: "En vivo",
        ctaLead: "Publicá un quest en",
        caption: "Escaneá para publicar",
      },
    },
    pt: {
      decor: "sunny",
      copy: {
        headline: "Precisa de mais\n*mãos?*",
        sub: "Publique a tarefa. As pessoas aparecem para ajudar.",
        chips: "Limpeza de praças, Arrecadações, Montagem de eventos, Panfletos",
        note: "Nossa IA ajuda a planejar.",
        rodri: true,
        sticker: "No ar",
        ctaLead: "Publique um quest em",
        caption: "Escaneie para publicar",
      },
    },
  },
  event: {
    en: {
      decor: "sunny",
      copy: {
        headline: "Come *help*\nthis Saturday",
        sub: "What we're doing, in one line.",
        chips: "Bring a friend, Two hours, Snacks on us",
        note: "",
        rodri: false,
        sticker: "Join us",
        ctaLead: "Sign up at",
        caption: "Scan to join",
      },
    },
    es: {
      decor: "sunny",
      copy: {
        headline: "Vení a *ayudar*\neste sábado",
        sub: "Qué vamos a hacer, en una línea.",
        chips: "Traé a alguien, Dos horas, Merienda incluida",
        note: "",
        rodri: false,
        sticker: "Sumate",
        ctaLead: "Anotate en",
        caption: "Escaneá para sumarte",
      },
    },
    pt: {
      decor: "sunny",
      copy: {
        headline: "Venha *ajudar*\nneste sábado",
        sub: "O que vamos fazer, em uma linha.",
        chips: "Traga um amigo, Duas horas, Lanche por nossa conta",
        note: "",
        rodri: false,
        sticker: "Participe",
        ctaLead: "Inscreva-se em",
        caption: "Escaneie para participar",
      },
    },
  },
};

export function presetFor(template: PosterTemplate, lang: PosterLang): Preset {
  return PRESETS[template][lang];
}

export function defaultConfig(template: PosterTemplate = "volunteers", lang: PosterLang = "en"): PosterConfig {
  const p = presetFor(template, lang);
  return {
    version: 1,
    template,
    lang,
    paper: "letter",
    decor: p.decor,
    copy: { ...p.copy },
    event: { date: "", place: "" },
    hostedBy: true,
    badge: { show: true, size: "m" },
    cats: template === "event" ? [] : SUNNY_CATS.map((c) => ({ ...c })),
    campaign: { mode: template === "event" ? "fixed" : "category", fixed: "" },
  };
}

/* ───────────────────────── normalize ───────────────────────── */

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;
const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);

/** Hard caps so a stored config can't blow up the canvas or the DB row. */
export const LIMITS = {
  headline: 120,
  sub: 160,
  chips: 200,
  note: 120,
  sticker: 40,
  ctaLead: 60,
  caption: 40,
  eventLine: 80,
  categoryName: 40,
  categories: 12,
  venues: 999,
} as const;

const clip = (s: string, max: number) => s.slice(0, max);

/**
 * Bring any stored/untrusted JSON to a complete, valid config. Unknown
 * enum values fall back to the default; strings are clipped to LIMITS.
 */
export function normalizeConfig(raw: unknown): PosterConfig {
  const r = isRecord(raw) ? raw : {};
  const template = (POSTER_TEMPLATES as readonly string[]).includes(str(r.template))
    ? (r.template as PosterTemplate)
    : "volunteers";
  const lang = (POSTER_LANGS as readonly string[]).includes(str(r.lang)) ? (r.lang as PosterLang) : "en";
  const d = defaultConfig(template, lang);

  const copyIn = isRecord(r.copy) ? r.copy : {};
  const copy: PosterCopy = {
    headline: clip(str(copyIn.headline, d.copy.headline), LIMITS.headline),
    sub: clip(str(copyIn.sub, d.copy.sub), LIMITS.sub),
    chips: clip(str(copyIn.chips, d.copy.chips), LIMITS.chips),
    note: clip(str(copyIn.note, d.copy.note), LIMITS.note),
    rodri: bool(copyIn.rodri, d.copy.rodri),
    sticker: clip(str(copyIn.sticker, d.copy.sticker), LIMITS.sticker),
    ctaLead: clip(str(copyIn.ctaLead, d.copy.ctaLead), LIMITS.ctaLead),
    caption: clip(str(copyIn.caption, d.copy.caption), LIMITS.caption),
  };

  const eventIn = isRecord(r.event) ? r.event : {};
  const badgeIn = isRecord(r.badge) ? r.badge : {};
  const campaignIn = isRecord(r.campaign) ? r.campaign : {};

  const cats: PosterCategory[] = Array.isArray(r.cats)
    ? r.cats.slice(0, LIMITS.categories).map((k, i) => {
        const c = isRecord(k) ? k : {};
        const venuesNum = typeof c.venues === "number" ? c.venues : parseInt(str(c.venues), 10);
        return {
          name: clip(str(c.name), LIMITS.categoryName),
          venues: Math.min(LIMITS.venues, Math.max(0, Number.isFinite(venuesNum) ? Math.trunc(venuesNum) : 0)),
          shape: (BADGE_SHAPES as readonly string[]).includes(str(c.shape))
            ? (c.shape as BadgeShape)
            : (BADGE_SHAPES[i % BADGE_SHAPES.length] ?? "circle"),
          color: str(c.color) in BADGE_COLORS ? (c.color as BadgeColor) : (BADGE_COLOR_KEYS[i % BADGE_COLOR_KEYS.length] ?? "yellow"),
        };
      })
    : d.cats;

  return {
    version: 1,
    template,
    lang,
    paper: str(r.paper) in PAPER ? (r.paper as PaperSize) : "letter",
    decor: r.decor === "sunny" || r.decor === "cool" ? r.decor : d.decor,
    copy,
    event: {
      date: clip(str(eventIn.date), LIMITS.eventLine),
      place: clip(str(eventIn.place), LIMITS.eventLine),
    },
    hostedBy: bool(r.hostedBy, d.hostedBy),
    badge: {
      show: bool(badgeIn.show, d.badge.show),
      size: str(badgeIn.size) in BADGE_SIZES ? (badgeIn.size as BadgeSize) : "m",
    },
    cats,
    campaign: {
      mode: campaignIn.mode === "fixed" ? "fixed" : "category",
      fixed: clip(str(campaignIn.fixed), LIMITS.categoryName),
    },
  };
}

/* ───────────────────────── slugs, variants, URLs ───────────────────────── */

/**
 * UTM-safe slug: accents folded, lower-case, `[a-z0-9-]` only. A stray
 * capital or accent would create a separate row in Vercel Analytics forever.
 */
export function slugify(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The posters to print: one per category, or one plain poster when there are none. */
export function posterVariants(cfg: Pick<PosterConfig, "cats">): PosterVariant[] {
  if (!cfg.cats.length) return [{ cat: null, num: 0 }];
  return cfg.cats.map((cat, i) => ({ cat, num: i + 1 }));
}

export const PUBLIC_ORIGIN = "https://www.wexare.com";

/** `[a-z2-9]{8}`: no 0/1/l/o lookalikes, so a code can be read off a reference sheet. */
export const CODE_ALPHABET = "abcdefghijkmnpqrstuvwxyz23456789";
export const CODE_RE = /^[a-z2-9]{8}$/;

export function newPosterCode(random: (n: number) => number = defaultRandom): string {
  let out = "";
  for (let i = 0; i < 8; i++) out += CODE_ALPHABET[random(CODE_ALPHABET.length)];
  return out;
}

function defaultRandom(n: number): number {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.getRandomValues) {
    const buf = new Uint32Array(1);
    c.getRandomValues(buf);
    return (buf[0] ?? 0) % n;
  }
  return Math.floor(Math.random() * n);
}

/** What the QR encodes: short, so the QR stays low-version and scannable at 41 mm. */
export function scanUrl(code: string, cat: PosterCategory | null, origin: string = PUBLIC_ORIGIN): string {
  const c = cat ? slugify(cat.name) : "";
  return `${origin}/p/${code}${c ? `?c=${c}` : ""}`;
}

export interface TrackingInput {
  template: PosterTemplate;
  campaignMode: "category" | "fixed";
  fixedCampaign: string;
  /** The scanned variant's category slug (already slugified or not). */
  category: string | null;
}

/** utm_campaign for one scan: the category, or the fixed value, or a safe default. */
export function campaignFor(t: TrackingInput): string {
  const fixed = slugify(t.fixedCampaign);
  if (t.campaignMode === "fixed") return fixed || "poster";
  return slugify(t.category) || fixed || "poster";
}

/**
 * Where a scan finally lands, UTMs attached. `path` is a relative app path
 * (the DB constrains it); its own query string is kept, UTM keys overwritten.
 */
export function trackedDestination(path: string, t: TrackingInput, origin: string = PUBLIC_ORIGIN): string {
  const safePath = isRelativePath(path) ? path : "/";
  const u = new URL(safePath, origin);
  u.searchParams.set("utm_source", "poster");
  u.searchParams.set("utm_medium", "qr");
  u.searchParams.set("utm_campaign", campaignFor(t));
  u.searchParams.set("utm_content", slugify(t.template));
  return u.toString();
}

/** A path inside the app: starts with one `/`, never `//host`, no whitespace. */
export function isRelativePath(path: string): boolean {
  return /^\/[^\s]*$/.test(path) && !path.startsWith("//") && !/^\/\\/.test(path);
}

/** The human-readable address printed in blue under the sticker. */
export function displayLink(path: string, origin: string = PUBLIC_ORIGIN): string {
  const host = origin.replace(/^https?:\/\//, "").replace(/^www\./, "");
  const clean = (isRelativePath(path) ? path : "/").split("?")[0] ?? "/";
  return clean === "/" ? host : host + clean;
}

/** File-name stem for exports. */
export function posterFileStem(name: string, cfg: Pick<PosterConfig, "template" | "paper">, v: PosterVariant): string {
  const base = slugify(name) || `${cfg.template}-${cfg.paper}`;
  const cat = v.cat ? `-${v.num}-${slugify(v.cat.name) || "category"}` : "";
  return `weXare-${base}${cat}`;
}
